import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { formatDate } from "../../utils/formatDate";
import "../../assets/styles/ReportsTable.css";
import "../../assets/styles/Badges.css";
import apiService from "../../services/apiService";
import { useAuth } from "../../context/AuthContext";
import OpportunityModal from "../modals/opportunityModal/OpportunityModal";
import { useToast } from "../../hooks/useToast";
import ProductsList from "../ProductsList";
import { Badge, Button } from "react-bootstrap";

const OpportunityTable = ({
  data,
  loading,
  requestSort,
  sortOrder,
  setLoading,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [towerInfo, setTowerInfo] = useState(null);
  const [towerList, setTowerList] = useState([]);
  const [opportunitiesWithTowers, setOpportunitiesWithTowers] = useState([]);
  const [selectedTower, setSelectedTower] = useState(null);
  const [towerId, setTowerId] = useState("");
  const [formattedTowerData, setformattedTowerData] = useState({});
  const [modalLoading, setModalLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast();
  const [connectionError, setConnectionError] = useState(false);
  const unmountedRef = useRef(false);

  const checkExistingTower = useCallback(
    async (opportunities) => {
      setLoading(true);
      try {
        if (opportunities !== undefined && !connectionError) {
          const promises = opportunities?.map((opportunity) =>
            apiService.get(`/tower/hasTower/${opportunity.data.OpportunityID}`)
          );
          const results = await Promise.all(promises);
          const towerInfo = [];
          results.forEach((result, index) => {
            towerInfo[opportunities[index].data.OpportunityID] =
              result.data && typeof result.data === "object";
          });
          setOpportunitiesWithTowers(towerInfo);
          setLoading(false);
          setConnectionError(false);
        }
      } catch (error) {
        console.error("Error fetching tower info:", error);
        setLoading(false);
        setConnectionError(true);
        // Only call addToast if the component is still mounted
        if (!unmountedRef.current) {
          addToast(error.message);
        }
      }
    },
    [setLoading, connectionError]
  );

  useEffect(() => {
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  useEffect(() => {
    if (!connectionError) checkExistingTower(data);
  }, [data, connectionError, checkExistingTower]);

  const handleCreateNewTower = () => {
    const projectData = {
      towerInfo: {
        generalTowerDescription: {
          serialNumber: "",
          customer_tower_designation:
            selectedProject.customer_tower_designation,
          submitter: user?.name || "Guest",
          submitter_email: user?.email || "",
          state: selectedProject.Primary_State,
          city: selectedProject.Primary_City,
          street: selectedProject.Primary_Street,
          zip: selectedProject.Primary_Code_Zip,
          model: "",
          manufacturer: "",
          tower_notes: "",
        },
        companyInfo: {
          company_name: selectedProject.Account_Name?.name,
        },
      },
      dealId: selectedProject.OpportunityID,
      status: "Pending",
    };

    navigate("/createTower", {
      state: {
        initialData: projectData,
        dealId: selectedProject.OpportunityID,
      },
    });
  };

  const changeTower = async (value) => {
    if (value === "create_new") {
      handleCreateNewTower();
    } else if (value !== "") {
      setModalLoading(true);
      processTowerData(findTowerBySerialNumber(towerList, value));

      setShowModal(true);

      // const tower = towerList.find((each) => each.serialNumber === value);
      // if (tower.towerInfo) {
      //   setformattedTowerData({});
      //   setTowerId(tower?.id);
      // } else {
      //   setformattedTowerData({});
      // }
      setModalLoading(false);
    }
  };

  const handleProjectClick = async (item) => {
    setSelectedProject(item);
    setModalLoading(true);
    try {
      const towerList = await apiService.get(
        `/tower/getTowerData/${item.OpportunityID}`
      );
      setTowerList(towerList?.data || []);
      console.log("Tower List:", towerList);

      if (towerList?.data && towerList?.data?.length > 0) {
        const m_towerInfo = towerList?.data?.sort(
          (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
        )[0];

        processTowerData(m_towerInfo);
      } else {
        setformattedTowerData({});
        setTowerId("");
      }
      setShowModal(true);
    } catch (error) {
      console.error("Error fetching tower data:", error);
      addToast("Failed to fetch tower data", "danger");
    } finally {
      setModalLoading(false);
    }
  };

  const processTowerData = (m_towerInfo) => {
    if (!m_towerInfo) {
      setformattedTowerData({});
      setTowerId("");
      return;
    }

    setSelectedTower(m_towerInfo);
    setTowerId(m_towerInfo?.id || "");

    if (m_towerInfo?.towerInfo) {
      // Extract GTD data from generalTowerDescription
      const gtdData = m_towerInfo.towerInfo.generalTowerDescription || {};
      if (!gtdData) {
        throw new Error("No general tower description found");
      }

      let formattedData = {
        ...m_towerInfo.towerInfo,
        generalTowerDescription: {
          ...m_towerInfo.towerInfo.generalTowerDescription,
        },
      };
      //since towerInfo.generalTowerDescription has some duplicate fields with towerInfo.components...
      formattedData = {
        ...formattedData,
        components: {
          ...m_towerInfo.towerInfo.components,
          structure: {
            ...formattedData.generalTowerDescription.structure,
            ...m_towerInfo.towerInfo.components?.structure,
          },
          louvers: {
            ...formattedData.generalTowerDescription.louvers,
            ...m_towerInfo.towerInfo.components?.louvers,
          },
          fill: {
            ...formattedData.generalTowerDescription.fill,
            ...m_towerInfo.towerInfo.components?.fill,
          },
        },
      };
      console.log("GTD Data:", formattedData);

      // Set the converted text directly with the GTD data
      setformattedTowerData(formattedData);
    } else {
      setformattedTowerData({});
    }
  };

  // Add the findTowerBySerialNumber function
  const findTowerBySerialNumber = (data, serialNumber) => {
    if (!data || !data || !Array.isArray(data)) {
      return null;
    }

    return data.find(
      (tower) =>
        tower.towerInfo &&
        tower.towerInfo.generalTowerDescription &&
        tower.towerInfo.generalTowerDescription.serialNumber === serialNumber
    );
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const getTowerData = async (dealId) => {
    try {
      const response = await apiService.get(`/tower/getTowerData/${dealId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching tower data:", error);
      return null;
    }
  };

  const handleImport = async (item) => {
    try {
      const existingTowerResponse = await getTowerData(item.OpportunityID);

      if (existingTowerResponse && existingTowerResponse.length > 0) {
        const mostRecentTower = existingTowerResponse[0];
        console.log("Most recent tower full data:", mostRecentTower);

        navigate("/createTower", {
          state: {
            initialData: mostRecentTower,
            dealId: item.OpportunityID,
            status: mostRecentTower.status,
            isUpdate: true,
            _id: mostRecentTower.id,
          },
        });
      } else {
        // No existing tower - use Zoho data
        console.log("No existing tower found - using Zoho data");
        const projectData = {
          towerInfo: {
            generalTowerDescription: {
              serialNumber: item.ATS_Tower_Serial_Number || "",
              customer_tower_designation: item.Customer_Tower_Designation,
              submitter: user?.name || "Guest",
              submitter_email: user?.email || "",
              state: item.Primary_State || "",
              city: item.Primary_City || "",
              street: item.Primary_Street || "",
              zip: item.Primary_Code_Zip || "",
              model: item.Model_Number || "",
              manufacturer: item.Manufacturer || "",
              tower_notes: item.Additional_Tower_Notes || "",
            },
            companyInfo: {
              company_name: item.Account_Name?.name || "",
            },
          },
          dealId: item.OpportunityID,
          status: "Pending",
        };

        navigate("/createTower", {
          state: {
            initialData: projectData,
            dealId: item.OpportunityID,
            status: projectData.status,
            isUpdate: false,
          },
        });
      }
    } catch (error) {
      console.error("Error in handleImport:", error);
      addToast("Error loading tower data", "danger");
    }
  };

  const groupedData = useMemo(() => {
    if (!data || !Array.isArray(data)) {
      return {};
    }
    return data.reduce((acc, item) => {
      const date = formatDate(item.data.Date_Bid_Due);
      if (date && date.trim() !== "") {
        if (!acc[date]) {
          acc[date] = [];
        }
        acc[date].push(item);
      }
      return acc;
    }, {});
  }, [data]);

  return (
    <div className="table-container">
      <table className="table table-striped custom-table-header">
        <thead className="sticky-header">
          <tr>
            <th onClick={() => requestSort("Date_Bid_Due")}>
              DATE BID DUE{" "}
              {sortOrder.key === "Date_Bid_Due" &&
                (sortOrder.direction === "asc" ? "↑" : "↓")}
            </th>
            <th>
              FORMAL RFQ{" "}
              {sortOrder.key === "Firm_RFQ" &&
                (sortOrder.direction === "asc" ? "↑" : "↓")}
            </th>
            <th onClick={() => requestSort("Deal_Name")}>
              OPPORTUNITY NAME{" "}
              {sortOrder.key === "Deal_Name" &&
                (sortOrder.direction === "asc" ? "↑" : "↓")}
            </th>
            <th onClick={() => requestSort("Stage")}>
              STAGE{" "}
              {sortOrder.key === "Stage" &&
                (sortOrder.direction === "asc" ? "↑" : "↓")}
            </th>

            <th onClick={() => requestSort("Parent_Account")}>
              PARENT COMPANY{" "}
              {sortOrder.key === "Parent_Account" &&
                (sortOrder.direction === "asc" ? "↑" : "↓")}
            </th>
            <th>
              COMPANY NAME{" "}
              {sortOrder.key === "Account_Name.name" &&
                (sortOrder.direction === "asc" ? "↑" : "↓")}
            </th>
            <th>
              ATS (PRODUCTS){" "}
              {sortOrder.key === "ATS_productss" &&
                (sortOrder.direction === "asc" ? "↑" : "↓")}
            </th>
            <th onClick={() => requestSort("Owner.name")}>
              OWNER{" "}
              {sortOrder.key === "Owner.name" &&
                (sortOrder.direction === "asc" ? "↑" : "↓")}
            </th>
            <th>ACTIONS</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan="10" className="text-center">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </td>
            </tr>
          ) : Object.keys(groupedData).length === 0 ? (
            <tr>
              <td colSpan="10" className="text-center">
                No Opportunity Data
              </td>
            </tr>
          ) : (
            Object.entries(groupedData).map(([date, items]) => (
              <React.Fragment key={date}>
                {items.map((each, index) => {
                  let item = each.data;
                  return (
                    <tr key={`opp-table-${item?.id}-${index}`}>
                      {index === 0 && (
                        <td rowSpan={items.length}>
                          {date} ({items.length})
                        </td>
                      )}
                      <td>{item.Firm_RFQ ? "True" : "False"}</td>
                      <td>
                        <Link onClick={() => handleProjectClick(item)}>
                          {item.Deal_Name}
                        </Link>
                      </td>
                      <td>
                        <Badge
                          bg=""
                          className={`px-3 py-2 rounded-pill ${
                            item.Stage === "Closed Won"
                              ? "bg-custom-won"
                              : item.Stage === "Budget"
                              ? "bg-custom-budget"
                              : item.Stage === "Lead"
                              ? "bg-custom-lead"
                              : item.Stage === "Actual"
                              ? "bg-custom-actual"
                              : item.Stage === "Pending Proposal Actual"
                              ? "bg-custom-actual"
                              : item.Stage === "Inactive"
                              ? "bg-custom-inactive"
                              : item.Stage === "Closed Lost"
                              ? "bg-custom-lost"
                              : item.Stage === "Pending Proposal Budget"
                              ? "bg-custom-budget"
                              : item.Stage === "Deleted"
                              ? "bg-custom-lost"
                              : "bg-secondary" // default color if none of the conditions match
                          }`}
                        >
                          {item.Stage}
                        </Badge>
                      </td>
                      <td>{item.Parent_Account}</td>
                      <td>{item.Account_Name?.name}</td>
                      <td>
                        <ProductsList products={item.ATS_productss} />
                      </td>
                      <td>{item.Owner?.name || "N/A"}</td>
                      <td>
                        <Button
                          variant={
                            opportunitiesWithTowers[item.OpportunityID]
                              ? "secondary"
                              : "primary"
                          }
                          onClick={() =>
                            handleImport(
                              item,
                              opportunitiesWithTowers[item.OpportunityID]
                            )
                          }
                        >
                          {opportunitiesWithTowers[item.OpportunityID]
                            ? "Edit Tower"
                            : "Create Tower"}
                        </Button>
                        {/* <Button
                          variant={
                            opportunitiesWithTowers[item.OpportunityID]
                              ? "secondary"
                              : "primary"
                          }
                          onClick={() =>
                            handleImport(
                              item,
                              opportunitiesWithTowers[item.OpportunityID]
                            )
                          }
                        >
                          {opportunitiesWithTowers[item.OpportunityID] ? (
                            <i className="bi bi-pencil-square"></i>
                          ) : (
                            <i className="bi bi-plus-circle"></i>
                          )}
                        </Button> */}
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ))
          )}
        </tbody>
      </table>
      <OpportunityModal
        show={showModal}
        handleClose={handleCloseModal}
        project={selectedProject}
        towerInfo={selectedTower}
        towerList={towerList}
        setTowerInfo={setSelectedTower}
        formattedTowerData={formattedTowerData}
        setformattedTowerData={setformattedTowerData}
        modalLoading={modalLoading}
        onChange={changeTower}
        towerId={towerId}
      />
    </div>
  );
};

export default OpportunityTable;
